import { create } from 'zustand';
import { watchlistService, WatchlistItem } from '../services/watchlistService';
import { useToastStore } from './useToastStore';

interface WatchlistState {
  // 状态
  items: WatchlistItem[];
  isLoading: boolean;
  error: string | null;
  watchedSymbols: Set<string>; // 用于快速检查是否已关注

  // Actions
  addToWatchlist: (symbol: string, instType: string) => Promise<void>;
  removeFromWatchlist: (symbol: string, instType: string) => Promise<void>;
  checkIsWatched: (symbol: string, instType: string) => boolean;
  refreshWatchlist: () => Promise<void>;
  clearError: () => void;
}

export const useWatchlistStore = create<WatchlistState>((set, get) => ({
  // 初始状态
  items: [],
  isLoading: false,
  error: null,
  watchedSymbols: new Set(),

  // 添加关注
  addToWatchlist: async (symbol: string, instType: string) => {
    set({ isLoading: true, error: null });

    try {
      const newItem = await watchlistService.addToWatchlist(symbol, instType);
      // 提示
      useToastStore.getState().show('关注成功', 'success');

      // 根据接口返回的数据手动维护列表
      const key = `${symbol}-${instType}`;
      set((state) => {
        // 检查是否已存在，避免重复添加
        const exists = state.items.some((item) => item.symbol === symbol && item.instType === instType);

        if (exists) {
          return state;
        }

        // 将新项添加到列表开头（因为后端是按 createdAt desc 排序的）
        // 确保 createdAt 是字符串格式
        const formattedItem: WatchlistItem = {
          ...newItem,
          createdAt:
            typeof newItem.createdAt === 'string'
              ? newItem.createdAt
              : (newItem.createdAt as any) instanceof Date
                ? (newItem.createdAt as Date).toISOString()
                : new Date().toISOString(),
        };

        const newItems = [formattedItem, ...state.items];

        const newWatchedSymbols = new Set(state.watchedSymbols);
        newWatchedSymbols.add(key);

        return { items: newItems, watchedSymbols: newWatchedSymbols };
      });
    } catch (error: any) {
      set({ error: error.message || '添加关注失败' });
      useToastStore.getState().show(error.message || '添加关注失败', 'error');
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  // 取消关注
  removeFromWatchlist: async (symbol: string, instType: string) => {
    set({ isLoading: true, error: null });

    try {
      await watchlistService.removeFromWatchlist(symbol, instType);
      useToastStore.getState().show('已取消关注', 'success');

      // 更新本地状态
      const key = `${symbol}-${instType}`;
      set((state) => {
        const newWatchedSymbols = new Set(state.watchedSymbols);
        newWatchedSymbols.delete(key);
        return { watchedSymbols: newWatchedSymbols };
      });

      // 刷新关注列表
      await get().refreshWatchlist();
    } catch (error: any) {
      set({ error: error.message || '取消关注失败' });
      useToastStore.getState().show(error.message || '取消关注失败', 'error');
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  // 检查是否已关注
  checkIsWatched: (symbol: string, instType: string) => {
    const key = `${symbol}-${instType}`;
    return get().watchedSymbols.has(key);
  },

  // 刷新关注列表
  refreshWatchlist: async () => {
    set({ isLoading: true, error: null });

    try {
      const items = await watchlistService.getWatchlist();

      // 更新关注符号集合
      const watchedSymbols = new Set<string>();
      items.forEach((item) => {
        watchedSymbols.add(`${item.symbol}-${item.instType}`);
      });

      set({ items, watchedSymbols });
    } catch (error: any) {
      set({ error: error.message || '获取关注列表失败' });
    } finally {
      set({ isLoading: false });
    }
  },

  // 清除错误
  clearError: () => {
    set({ error: null });
  },
}));
